'use client';

import { useRef, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Cropper, { Area } from 'react-easy-crop';
import { Upload, X, Check, RotateCw, Link as LinkIcon, Globe, Copy, Zap, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import { usePageBuilderContext } from '../page-builder-context';
import { uploadLogo } from '@/lib/services/uploadService';
import { MaterialSpinner } from '@/components/ui/material-spinner';
import { cn } from '@/lib/utils';

// ============================================================================
// LOGO UPLOAD COMPONENT (COMPACT)
// ============================================================================

const LOGO_UPLOAD_KEY = 'header-logo';

function LogoUploadCompact() {
  const { formData, updateHeader, errors, uploads, startUpload, retryUpload, dismissUpload } =
    usePageBuilderContext();
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cropper state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const pending = uploads[LOGO_UPLOAD_KEY];
  // The freshly-cropped local preview wins over the stored logo until the
  // background upload settles (see use-async-uploads)
  const preview = pending?.previewUrl ?? formData.header.logo?.originalUrl ?? null;
  const hasError = errors.has('header.logo');

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const openCropper = useCallback((file: File) => {
    const url = URL.createObjectURL(file);
    setImageToCrop(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropModalOpen(true);
  }, []);

  const handleCropSave = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    try {
      const croppedFile = await getCroppedLogo(imageToCrop, croppedAreaPixels);
      setCropModalOpen(false);
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);

      // Optimistic: the cropped logo shows instantly, upload runs in the background
      startUpload(LOGO_UPLOAD_KEY, croppedFile, uploadLogo, (data) => ({
        warmUrl: data.originalUrl,
        commit: () => updateHeader('logo', data),
      }));
    } catch (e) {
      console.error('Error cropping logo:', e);
      toast.error(e instanceof Error ? e.message : 'Ошибка обработки');
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
  };

  const handleFile = useCallback((file: File) => {
    if (file.type.startsWith('image/')) {
      openCropper(file);
    }
  }, [openCropper]);

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          handleFile(file);
          toast.info('Изображение вставлено');
        }
        break;
      }
    }
  }, [handleFile]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onEnter = () => document.addEventListener('paste', handlePaste);
    const onLeave = () => document.removeEventListener('paste', handlePaste);

    container.addEventListener('mouseenter', onEnter);
    container.addEventListener('mouseleave', onLeave);

    return () => {
      container.removeEventListener('mouseenter', onEnter);
      container.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    if (pending) {
      // Drop the in-flight/failed upload; the previous logo (if any) comes back
      dismissUpload(LOGO_UPLOAD_KEY);
    } else {
      updateHeader('logo', null);
    }
  };

  const isUploading = pending?.status === 'uploading';
  const uploadFailed = pending?.status === 'error';

  return (
    <>
      <div
        ref={containerRef}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('pb-logo-input')?.click()}
        className={cn(
          'relative w-20 h-20 rounded-xl border-2 border-dashed transition-all duration-200 overflow-hidden group flex-shrink-0',
          'flex items-center justify-center cursor-pointer',
          isDragging && 'border-foreground/30 bg-muted/50 scale-105',
          !isDragging && 'border-border',
          preview && !uploadFailed && 'border-0',
          (hasError || uploadFailed) && 'border-solid border-destructive'
        )}
      >
        <input
          type="file"
          id="pb-logo-input"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
          className="sr-only"
        />

        {preview ? (
          <>
            <img
              src={preview}
              alt="Logo"
              className="w-full h-full object-cover"
            />

            {/* Upload in flight — logo stays fully visible, only a corner
                badge hints that it's still uploading in the background */}
            {isUploading && (
              <div className="absolute bottom-1 right-1 rounded-full bg-card/95 p-1 z-10">
                <MaterialSpinner className="h-3.5 w-3.5" />
              </div>
            )}

            {/* Upload failed — retry in place, X dismisses the failed attempt */}
            {uploadFailed && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    retryUpload(LOGO_UPLOAD_KEY);
                  }}
                  className="flex flex-col items-center gap-0.5 text-white"
                  title="Не удалось загрузить — повторить"
                >
                  <RotateCw className="h-4 w-4" />
                  <span className="text-[10px] font-medium">Повторить</span>
                </button>
              </div>
            )}

            {!pending && (
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-xs font-medium">Изменить</span>
              </div>
            )}
            {!isUploading && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className={cn(
                  'absolute top-1 right-1 w-4 h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center transition-opacity z-20',
                  uploadFailed ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-center">
            <Upload className="h-5 w-5 text-foreground-tertiary" />
            <span className="text-[10px] font-medium text-foreground-tertiary">1:1</span>
          </div>
        )}
      </div>

      {/* Crop Modal */}
      {cropModalOpen && imageToCrop && (
        <div className="fixed inset-0 z-50 bg-black">
          <div className="absolute inset-0 bottom-16">
            <Cropper
              image={imageToCrop}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={true}
              cropShape="rect"
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-black/90 backdrop-blur border-t border-white/10 px-4 py-3">
            <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs text-white/60">−</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
                <span className="text-xs text-white/60">+</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleCropSave}
                  className="px-4 py-1.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary-hover rounded-lg"
                >
                  Готово
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// CROPPING HELPER
// ============================================================================

async function getCroppedLogo(imageSrc: string, pixelCrop: Area): Promise<File> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      const file = new File([blob], `logo-${Date.now()}.webp`, { type: 'image/webp' });
      resolve(file);
    }, 'image/webp', 0.99);
  });
}

// ============================================================================
// SLUG AUTO-GENERATION (TRANSLITERATION)
// ============================================================================

const CYR_MAP: Record<string, string> = {
  'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'yo',
  'ж':'zh','з':'z','и':'i','й':'y','к':'k','л':'l','м':'m',
  'н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
  'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch',
  'ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya',
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .split('')
    .map(c => CYR_MAP[c] ?? c)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

// ============================================================================
// FIXED HEADER
// ============================================================================

export function FixedHeader() {
  const { formData, updateHeader, errors, isSaving, mode } = usePageBuilderContext();
  const { slug, name, tagline, description, websiteUrl } = formData.header;

  // Name & slug are immutable once a product exists — the edit endpoint ignores
  // them — so they are shown read-only in edit mode.
  const isEdit = mode === 'edit';

  const taglineLength = tagline.length;
  const minTaglineLength = 6;
  const maxTaglineLength = 100;

  const descriptionLength = description.length;
  const minDescriptionLength = 20;
  const maxDescriptionLength = 500;

  // Slug auto-generation
  const [isCustomSlug, setIsCustomSlug] = useState(() => {
    if (!slug) return false;
    // If existing slug doesn't match auto-generated, user customized it
    return slug !== slugify(name);
  });

  const prevNameRef = useRef(name);

  useEffect(() => {
    if (isCustomSlug) return;
    if (name === prevNameRef.current) return;
    prevNameRef.current = name;
    updateHeader('slug', slugify(name));
  }, [name, isCustomSlug, updateHeader]);

  const handleSwitchToCustom = () => {
    setIsCustomSlug(true);
  };

  const handleSwitchToAuto = () => {
    setIsCustomSlug(false);
    prevNameRef.current = ''; // force regeneration
    updateHeader('slug', slugify(name));
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5">
      {/* Auto-save indicator */}
      <AnimatePresence>
        {isSaving && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute top-3 right-3 flex items-center gap-1.5 text-xs text-success"
          >
            <Check className="h-3 w-3" />
            <span>Сохранено</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slug — URL builder */}
      <div>
        {/* Header row: label + mode toggle + copy */}
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-foreground-secondary">
            Адрес страницы
          </label>
          <div className="flex items-center gap-3">
            {/* Auto / Custom toggle — hidden while editing (slug is locked) */}
            {!isEdit && (
              <div className="flex items-center rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={handleSwitchToAuto}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors',
                    !isCustomSlug
                      ? 'bg-primary-subtle text-primary-text'
                      : 'text-foreground-tertiary hover:text-foreground-secondary'
                  )}
                >
                  <Zap className="h-3 w-3" />
                  Авто
                </button>
                <button
                  type="button"
                  onClick={handleSwitchToCustom}
                  className={cn(
                    'flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors',
                    isCustomSlug
                      ? 'bg-primary-subtle text-primary-text'
                      : 'text-foreground-tertiary hover:text-foreground-secondary'
                  )}
                >
                  <Pencil className="h-3 w-3" />
                  Свой
                </button>
              </div>
            )}
            {/* Copy */}
            {slug && !errors.has('header.slug') && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`peekproduct.tech/products/${slug}`);
                  toast.success('Ссылка скопирована');
                }}
                className="text-foreground-tertiary hover:text-foreground-secondary transition-colors"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* URL bar */}
        <div className={cn(
          'flex items-center rounded-xl border transition-colors',
          errors.has('header.slug')
            ? 'border-destructive/50 bg-destructive/5'
            : 'border-border bg-muted/20'
        )}>
          <div className="flex items-center gap-2 pl-3 pr-2 py-2.5 select-none shrink-0">
            <Globe className="h-3.5 w-3.5 text-foreground-tertiary" />
            <span className="text-[13px] text-foreground-tertiary font-mono tracking-tight">
              peekproduct.tech/products/
            </span>
          </div>
          <div className="h-5 w-px bg-border shrink-0" />
          {isCustomSlug && !isEdit ? (
            <input
              value={slug}
              onChange={(e) => {
                const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                updateHeader('slug', value);
              }}
              placeholder="my-product"
              maxLength={60}
              className={cn(
                'flex-1 bg-transparent px-3 py-2.5 text-[13px] font-mono font-medium tracking-tight',
                'placeholder:text-foreground-tertiary/50 focus:outline-none',
                errors.has('header.slug') ? 'text-destructive' : 'text-foreground'
              )}
            />
          ) : (
            <span className={cn(
              'flex-1 px-3 py-2.5 text-[13px] font-mono tracking-tight truncate',
              slug ? 'text-foreground font-medium' : 'text-foreground-tertiary/50'
            )}>
              {slug || 'введите название...'}
            </span>
          )}
          {slug && !errors.has('header.slug') && (
            <div className="pr-3 shrink-0">
              <Check className="h-3.5 w-3.5 text-success" />
            </div>
          )}
        </div>

        {/* Helper text */}
        {errors.has('header.slug') ? (
          <p className="text-[11px] text-destructive mt-1.5">
            {errors.get('header.slug')?.[0]}
          </p>
        ) : isEdit ? (
          <p className="text-[11px] text-foreground-tertiary mt-1.5">
            Адрес страницы нельзя изменить после создания
          </p>
        ) : !isCustomSlug ? (
          <p className="text-[11px] text-foreground-tertiary mt-1.5">
            Генерируется автоматически из названия
          </p>
        ) : (
          <p className="text-[11px] text-foreground-tertiary mt-1.5">
            Только строчные латинские буквы, цифры и дефис
          </p>
        )}
      </div>

      {/* Logo + Name + Tagline row */}
      <div className="flex items-start gap-5">
        <LogoUploadCompact />

        <div className="flex-1 space-y-3">
          {/* Product name */}
          <div>
            <Input
              value={name}
              onChange={(e) => updateHeader('name', e.target.value)}
              placeholder="Название продукта"
              readOnly={isEdit}
              title={isEdit ? 'Название нельзя изменить после создания' : undefined}
              className={cn(
                'text-xl font-bold border-none bg-transparent px-3 h-auto focus-visible:ring-0 placeholder:text-foreground-tertiary',
                errors.has('header.name') && 'text-destructive',
                isEdit && 'cursor-not-allowed opacity-90'
              )}
              maxLength={50}
            />
            {isEdit ? (
              <p className="text-[11px] text-foreground-tertiary mt-1 px-3">
                Название нельзя изменить после создания
              </p>
            ) : errors.has('header.name') && (
              <p className="text-xs text-destructive mt-1">
                {errors.get('header.name')?.[0]}
              </p>
            )}
          </div>

          {/* Tagline */}
          <div>
            <Input
              value={tagline}
              onChange={(e) => updateHeader('tagline', e.target.value)}
              placeholder="Краткое описание в одну строку..."
              className={cn(
                'text-base border-none bg-transparent px-3 h-auto focus-visible:ring-0 text-foreground-secondary placeholder:text-foreground-tertiary',
                errors.has('header.tagline') && 'text-destructive'
              )}
              maxLength={maxTaglineLength}
            />
            <div className="flex items-center justify-between px-3 mt-1">
              {errors.has('header.tagline') ? (
                <p className="text-xs text-destructive">
                  {errors.get('header.tagline')?.[0]}
                </p>
              ) : taglineLength > 0 && taglineLength < minTaglineLength ? (
                <p className="text-xs text-foreground-tertiary">
                  Ещё {minTaglineLength - taglineLength} симв.
                </p>
              ) : (
                <span />
              )}
              {taglineLength > 0 && (
                <span className={cn(
                  'text-xs tabular-nums',
                  taglineLength < minTaglineLength
                    ? 'text-foreground-tertiary'
                    : taglineLength > maxTaglineLength * 0.9
                      ? 'text-warning'
                      : 'text-foreground-tertiary'
                )}>
                  {taglineLength}/{maxTaglineLength}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Description - Bento Box */}
      <div className={cn(
        'bg-muted/30 rounded-lg p-4 border border-border/50 transition-colors',
        errors.has('header.description') && 'border-destructive/50 bg-destructive/5'
      )}>
        <textarea
          value={description}
          onChange={(e) => updateHeader('description', e.target.value)}
          placeholder="Опишите ваш продукт в 2-3 предложениях. Что он делает? Какую проблему решает?"
          rows={3}
          maxLength={maxDescriptionLength}
          className={cn(
            'w-full bg-transparent resize-none text-sm leading-relaxed',
            'placeholder:text-foreground-tertiary text-foreground-secondary',
            'focus:outline-none focus:ring-0',
            errors.has('header.description') && 'text-destructive'
          )}
        />
        <div className="flex items-center justify-between mt-2">
          {errors.has('header.description') ? (
            <p className="text-xs text-destructive">
              {errors.get('header.description')?.[0]}
            </p>
          ) : descriptionLength > 0 && descriptionLength < minDescriptionLength ? (
            <span className="text-xs text-foreground-tertiary">
              Ещё {minDescriptionLength - descriptionLength} симв.
            </span>
          ) : (
            <span className="text-xs text-foreground-tertiary">
              Описание продукта
            </span>
          )}
          <span className={cn(
            'text-xs tabular-nums',
            descriptionLength < minDescriptionLength && descriptionLength > 0
              ? 'text-foreground-tertiary'
              : descriptionLength > maxDescriptionLength * 0.9
                ? 'text-warning'
                : 'text-foreground-tertiary'
          )}>
            {descriptionLength}/{maxDescriptionLength}
          </span>
        </div>
      </div>

      {/* Website URL */}
      <div className="flex items-center gap-2 pt-2 border-t border-border/50">
        <LinkIcon className="h-4 w-4 text-foreground-tertiary flex-shrink-0" />
        <Input
          value={websiteUrl}
          onChange={(e) => updateHeader('websiteUrl', e.target.value)}
          placeholder="https://yourproduct.com"
          type="url"
          className={cn(
            'border-none bg-transparent px-3 h-auto focus-visible:ring-0 text-sm text-foreground-secondary placeholder:text-foreground-tertiary',
            errors.has('header.websiteUrl') && 'text-destructive'
          )}
        />
      </div>
    </div>
  );
}
